<template>
    <nav class="ds-pagination-controls" aria-label="Pagination Navigation">
        <button
            v-show="page > FIRST && last > nonCollapsibleAmount"
            :class="{ 'arrow-disabled': page < FIRST }"
            class="arrow arrow-left"
            @click="previous"
        >
            <DSIcon code="default-line/arrow-left" />
        </button>
        <button
            class="link"
            :class="{'link-active': page === FIRST}"
            @click="changePage(FIRST)"
        >
            {{ FIRST }}
        </button>
        <DSIcon
            v-if="pageCount >= amountOfVisibleMidPages && visibleMidPages[0] !== FIRST + 1 && last > nonCollapsibleAmount"
            class="more-indicator"
            code="default-line/more-horizontal"
        />
        <button
            v-for="midPage in visibleMidPages"
            :key="midPage"
            class="link"
            :class="{'link-active': page === midPage}"
            :aria-label="midPage"
            @click="changePage(midPage)"
        >
            {{ midPage }}
        </button>
        <DSIcon
            v-if="pageCount > amountOfVisibleMidPages && visibleMidPages.at(-1) !== last - 1 && last > nonCollapsibleAmount"
            class="more-indicator"
            code="default-line/more-horizontal"
        />
        <button
            v-if="last !== FIRST"
            class="link last"
            :class="{'link-active': page === last}"
            :aria-label="last"
            @click="changePage(last)"
        >
            {{ last }}
        </button>
        <button
            v-show="page < last && last > nonCollapsibleAmount"
            :class="{ 'arrow-disabled': page >= last }"
            class="arrow arrow-right"
            @click="next"
        >
            <DSIcon code="default-line/arrow-right" />
        </button>
    </nav>
</template>

<script setup>
import { useNuxtApp, computed } from '#imports';
const { $dsc } = useNuxtApp();

const props = defineProps({
    modelValue: {
        type: Number,
        required: true,
    },
    modelModifiers: {
        type: Object,
        default: () => ({}),
    },
    pageCount: {
        type: Number,
        required: true,
    },
    scrollTop: {
        type: [Number, Object],
        default: null,
    },
    amountOfVisibleMidPages: {
        type: Number,
        default: 3,
        validator: v => v >= 3,
    },
});

const emit = defineEmits(['update:modelValue']);

const FIRST = 1;

const last = computed(() => props.pageCount);
const shift = computed(() => props.modelModifiers['zero-index'] ? 1 : 0);
const page = computed(() => props.modelValue + shift.value);
const firstLastBtnAmount = computed(() => FIRST !== last.value ? 2 : 1);
const nonCollapsibleAmount = computed(() => props.amountOfVisibleMidPages + firstLastBtnAmount.value);

const changePage = async page => {
    if (page !== page.value) {
        emit('update:modelValue', page - shift.value);

        const opts = typeof props.scrollTop === 'number' ? { top: props.scrollTop } : props.scrollTop;
        await $dsc.windowScrollTo(opts);
    }
};

const visibleMidPages = computed(() => {
    if (last.value <= (props.amountOfVisibleMidPages + firstLastBtnAmount.value)) return [...Array(last.value - firstLastBtnAmount.value)].map((_, i) => i + FIRST + 1);

    const isMidPage = pageNum => pageNum > FIRST && pageNum < last.value;
    const getPage = i => page.value - props.amountOfVisibleMidPages + Math.round(props.amountOfVisibleMidPages / 2) + i;

    return [...Array(props.amountOfVisibleMidPages)].flatMap((_, i) => {
        const currentPage = getPage(i);

        return isMidPage(currentPage) ? currentPage : [];
    });
});
const next = () => {
    if (page.value < last.value) changePage(page.value + 1);
};
const previous = () => {
    if (page.value > FIRST) changePage(page.value - 1);
};
</script>

<style scoped>
.ds-pagination-controls {
  display: flex;
  align-items: center;
}
.ds-pagination-controls .link {
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 600;
  color: var(--color-base-grey-600);
  border: 0;
  border-radius: 50%;
  padding: 1rem;
  width: 32px;
  height: 32px;
  background-color: var(--color-base-white-default);
  display: flex;
  justify-content: center;
  align-items: center;
}
.ds-pagination-controls .link:hover {
  color: var(--color-base-black-default);
  background-color: var(--color-base-grey-100);
}
.ds-pagination-controls .link:active {
  color: var(--color-base-black-default);
  background-color: var(--color-base-grey-200);
}
.ds-pagination-controls .link-active {
  background-color: var(--color-primary-400);
  color: var(--color-base-black-default);
}
.ds-pagination-controls .link-active:hover {
  color: var(--color-base-black-default);
  background-color: var(--color-primary-400);
}
.ds-pagination-controls .more-indicator {
  color: var(--color-base-grey-600);
  margin: 0.5rem;
}
.ds-pagination-controls .arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-base-white-default);
  color: var(--color-base-grey-600);
  height: 32px;
  width: 32px;
  border-radius: 50%;
}
.ds-pagination-controls .arrow:hover {
  color: var(--color-base-black-default);
  background-color: var(--color-base-grey-100);
}
.ds-pagination-controls .arrow:active {
  color: var(--color-base-black-default);
  background-color: var(--color-base-grey-200);
}
.ds-pagination-controls .arrow > * {
  flex: 0 0 auto;
}
.ds-pagination-controls .arrow-disabled {
  pointer-events: none;
  opacity: 0.25;
}
</style>
